/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Initial clientStorage helper function.
 * Using in client browser ONLY!
 *
 * Usage:
 * const clientStorage = new ClientStorage('namespace', true) -- true: localStorage / flase : sessionStorage / default is true
 *
 * Example:
 * const tradingviewStorage = new ClientStorage('tradingview');
 * tradingviewStorage.update('charts', this.charts);
 */

import { isBrowser } from '../func/helper';

type ServerStorage = {
  storage: Map<string, any>;
  setItem: (key: string, value: any) => any;
  getItem: (key: string) => any;
  removeItem: (key: string) => any;
  clear: () => void;
};

const serverStorage: ServerStorage = {
  storage: new Map(),
  setItem(key, value) {
    return this.storage.set(key, value);
  },
  getItem(key) {
    return this.storage.get(key);
  },
  removeItem(key) {
    return this.storage.delete(key);
  },
  clear() {
    return this.storage.clear();
  },
};

export default class ClientStorage {
  namespace: string;
  permanent: boolean;
  storage: typeof localStorage | typeof sessionStorage | ServerStorage;

  constructor(namespace: string, permanent = true) {
    this.namespace = namespace;
    this.permanent = permanent;

    if (isBrowser()) {
      if (this.permanent) {
        this.storage = localStorage;
      } else {
        this.storage = sessionStorage;
      }
    } else {
      this.storage = serverStorage;
    }
  }

  private set<T>(itemName: string, itemValue: T) {
    return this.storage?.setItem(`${this.namespace}-${itemName}`, JSON.stringify(itemValue));
  }

  add<T>(itemName: string, itemValue: T) {
    if (!this.get(itemName)) {
      return this.set(itemName, itemValue);
    } else {
      throw new Error(`Storage value: ${itemName} existed!`);
    }
  }

  update<T>(itemName: string, itemValue: T) {
    if (this.get(itemName)) {
      return this.set(itemName, itemValue);
    } else {
      return this.add(itemName, itemValue);
    }
  }

  get(itemName: string) {
    const result = this.storage?.getItem(`${this.namespace}-${itemName}`);
    if (result) {
      return JSON.parse(result as string);
    } else {
      return false;
    }
  }

  remove(itemName: string) {
    return this.storage?.removeItem(`${this.namespace}-${itemName}`);
  }

  clear() {
    return this.storage?.clear();
  }
}
